import { getOperationName } from '@apollo/client/utilities'
import { Form, FormInstance, Modal } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  GetBasicProfileAboutMeDocument,
  GetUserPageInfoDocument,
  useGetUserPageInfoQuery,
  useUserInfoEditModalUpdateMutation,
} from '@/generated/graphql'
import { UserInfoContext } from '@/modules/me/me.container'
import { Address, Cities, Floors } from '@/shares/i18n-mapping'

import { UserInfoProps } from '../user-info.interface'
import { UserInfoEditModalBackground } from './user-info-edit-modal-background.component'
import { UserInfoEditModalForm, UserInfoEditModalFormProps } from './user-info-edit-modal-form.component'
import { UserInfoEditModalHobby } from './user-info-edit-modal-hobby.component'
import { UserInfoEditModalSkill } from './user-info-edit-modal-skill.component'

export const UserInfoEditModalInner: React.FC<UserInfoProps & { form: FormInstance }> = ({
  form,
  userId,
  viewerId,
  readonly,
}) => {
  const { data } = useGetUserPageInfoQuery({
    variables: {
      userId,
      viewedUserPagination: {
        last: 500,
      },
    },
  })

  const basicProfile = data?.user.basicProfile
  const companyProfile = data?.user.companyProfile

  const defaultCity = companyProfile?.location.value || Object.entries(Cities)[0][0]
  const defaultAddress = companyProfile?.address || Object.entries(Address[defaultCity])[0][0]
  const defaultFloor = companyProfile?.floor || Object.entries(Floors[defaultAddress])[0][0]

  const formInitialValues = {
    location: defaultCity,
    address: defaultAddress,
    floor: defaultFloor,
    seatNumber: basicProfile?.seatNumber.value || '',
    aboutMe: basicProfile?.aboutMe.value || '',
    linkedin: basicProfile?.linkedin.value || '',
    github: basicProfile?.github.value || '',
  }

  return (
    <div>
      <UserInfoEditModalForm form={form} initialValues={formInitialValues} />
      <UserInfoEditModalBackground userId={userId} readonly={readonly} />
      <UserInfoEditModalSkill userId={userId} readonly={readonly} viewerId={viewerId} />
      <UserInfoEditModalHobby userId={userId} readonly={readonly} viewerId={viewerId} />
    </div>
  )
}

export const UserInfoEditModal: React.FC<UserInfoProps> = ({ userId, readonly, viewerId }) => {
  const { t } = useTranslation()
  const { isGlobalEdit, setIsGlobalEdit } = React.useContext(UserInfoContext)

  const [form] = Form.useForm<UserInfoEditModalFormProps>()

  const [userInfoEditUpdate] = useUserInfoEditModalUpdateMutation({
    refetchQueries: [getOperationName(GetUserPageInfoDocument)!, getOperationName(GetBasicProfileAboutMeDocument)!],
  })

  const onOK = async () => {
    const values = await form.validateFields()
    userInfoEditUpdate({
      variables: {
        input: {
          ...values,
        },
      },
    })
    setIsGlobalEdit(false)
  }

  return (
    <Modal
      title={t('user.info.edit')}
      visible={isGlobalEdit}
      onCancel={() => setIsGlobalEdit(false)}
      onOk={() => onOK()}
      closable={false}
      destroyOnClose
    >
      <UserInfoEditModalInner form={form} userId={userId} readonly={readonly} viewerId={viewerId} />
    </Modal>
  )
}
